import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { EditOutlined, Search, Sensors } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Typography,
  styled,
} from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { RootState } from '~redux/rootReducer'
import { Process1, ProcessFilterStatusEnumType } from '~types'

interface StartProcessDialogProps {
  open: boolean
  onClose: () => void
}

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
    paddingTop: '5vh',
  },
  '& .MuiDialog-paper': {
    maxWidth: '600px',
    width: '100%',
    margin: '16px',
    borderRadius: '16px',
    height: '80vh',
  },
}))

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const StyledList = styled(List)(({ theme }) => ({
  padding: theme.spacing(2),
  overflow: 'auto',
  height: 'calc(80vh - 140px)', // Subtract header and search bar height
}))

const StyledChip = styled(Chip)<{ selected?: boolean; coloring?: 'live' | 'draft' }>(
  ({ theme, selected, coloring }) => ({
    backgroundColor: selected ? theme.palette.grey[100] : undefined,
    '& .MuiChip-icon': {
      color: selected
        ? coloring === 'live'
          ? theme.palette.success.main
          : theme.palette.text.primary
        : theme.palette.grey[400],
    },
    '& .MuiChip-label': {
      color: selected
        ? coloring === 'live'
          ? theme.palette.success.main
          : theme.palette.text.primary
        : theme.palette.grey[600],
    },
  }),
)

const StartProcessDialog: React.FC<StartProcessDialogProps> = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [processes, setProcesses] = useState<Process1[]>([])
  const [selectedStatus, setSelectedStatus] = useState<ProcessFilterStatusEnumType>('live')
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const user = useSelector((state: RootState) => state?.auth.user)
  const navigate = useNavigate()

  const loadProcesses = async (): Promise<void> => {
    if (!userId) return

    try {
      const response = await apiZodios.cases_listProcessesStats({
        user_id: userId,
        process_filter: {
          query: searchQuery || undefined,
          copyable_only: false,
          managed_only: true,
          status: [selectedStatus],
        },
      })
      setProcesses(response.processes)
    } catch (error) {
      console.error('Error loading processes:', error)
    }
  }

  useEffect(() => {
    if (open) {
      loadProcesses()
    }
  }, [open, searchQuery, selectedStatus])

  const handleProcessClick = async (process: Process1): Promise<void> => {
    if (!userId) return

    try {
      let response
      if (process.status?.toLowerCase() === 'draft') {
        response = await apiZodios.cases_createTestCase({
          user_id: userId,
          name: process.name,
          owner: process.owner.id,
          process_id: process.id,
        })
      } else {
        response = await apiZodios.cases_createCase({
          user_id: userId,
          name: process.name,
          status: 'Open',
          organization_identity_id: process.owner.id,
        })
      }
      await apiZodios.cases_editCase({
        case_id: response.case_id,
        user_id: userId,
        assignee: user?.identity_id,
      })
      await apiZodios.cases_attachProcess({
        user_id: userId,
        process_id: process.id,
        case_id: response.case_id,
      })

      if (response.handle) {
        navigate(`/cases/${response.handle}`)
      }

      onClose()
    } catch (error) {
      console.error('Error creating case:', error)
    }
  }

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        <Typography variant='h6'>Processes</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <StyledChip
            size='small'
            label='Live'
            icon={<Sensors sx={{ fontSize: 16 }} />}
            selected={selectedStatus === 'live'}
            coloring='live'
            onClick={() => setSelectedStatus('live')}
            sx={{ minWidth: 85 }}
          />
          <StyledChip
            size='small'
            label='Draft'
            icon={<EditOutlined sx={{ fontSize: 16 }} />}
            selected={selectedStatus === 'draft'}
            coloring='draft'
            onClick={() => setSelectedStatus('draft')}
            sx={{ minWidth: 85 }}
          />
        </Box>
      </StyledDialogTitle>

      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          placeholder='Search processes...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search />
              </InputAdornment>
            ),
          }}
          size='small'
        />
      </Box>

      <StyledList>
        {processes.length > 0 ? (
          processes.map((process) => (
            <ListItem key={process.id} disablePadding>
              <ListItemButton
                onClick={() => handleProcessClick(process)}
                sx={{ borderRadius: 2, mb: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {process.status?.toLowerCase() === 'live' ? (
                    <Sensors sx={{ color: 'success.main', mr: 2 }} />
                  ) : (
                    <EditOutlined sx={{ mr: 2 }} />
                  )}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant='body1' sx={{ fontWeight: 500 }}>
                      {process.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Avatar
                        src={process.owner.image || ''}
                        alt={process.owner.name}
                        sx={{ width: 20, height: 20 }}
                      />
                      <Typography variant='body2' color='text.secondary'>
                        {process.owner.name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ListItemButton>
            </ListItem>
          ))
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
            }}
          >
            <Typography>No processes found</Typography>
          </Box>
        )}
      </StyledList>
    </StyledDialog>
  )
}

export default StartProcessDialog
